import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import { playOutline, trashOutline, cubeOutline } from 'ionicons/icons';
import { timeAgo } from '../../utils/timeAgo';
import HoverVideo from '../../components/HoverVideo/HoverVideo';
import TerminalDemo from '../../components/atoms/TerminalDemo/TerminalDemo';
import { SyncingBadge } from '../../components/atoms';
import api from '../../services/api';
import './ProjectRunsList.css';

const ProjectRunsList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { project, setToast } = useOutletContext(); // Get project from parent context
  const [executions, setExecutions] = useState([]);
  const [executionsLoading, setExecutionsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextKey, setNextKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentEvents, setCurrentEvents] = useState({});
  
  // Get collection filter from URL params
  const searchParams = new URLSearchParams(location.search);
  const collectionFilter = searchParams.get('collection');

  useEffect(() => {
    fetchExecutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, collectionFilter]);

  const fetchExecutions = async (isLoadingMore = false) => {
    try {
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setExecutionsLoading(true);
      }

      const params = new URLSearchParams({
        projectId,
        limit: '20',
        executionType: 'test'
      });
      if (collectionFilter) {
        params.append('collection', collectionFilter);
      }
      if (isLoadingMore && nextKey) {
        params.append('lastKey', nextKey);
      }

      const data = await api.get(`/executions?${params.toString()}`);
      
      const testRunsOnly = (data.executions || []).filter(exec => {
        if (!exec.executionId) return false;
        const isTicketRun = exec.type === 'analysis' || exec.type === 'ticket_implementation';
        return !isTicketRun && exec.specPath;
      });
      
      if (isLoadingMore) {
        setExecutions(prev => [...prev, ...testRunsOnly]);
      } else {
        setExecutions(testRunsOnly);
      }
      setNextKey(data.nextKey || null);
      setHasMore(data.hasMore || false);
    } catch (error) {
      console.error('Error fetching executions:', error);
    } finally {
      setExecutionsLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore && nextKey) {
      fetchExecutions(true);
    }
  };

  const handleExecutionClick = (executionId) => {
    navigate(`/projects/${projectId}/runs/${executionId}`);
  };

  const handleDeleteExecution = async (executionId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this execution?')) {
      return;
    }

    try {
      await api.delete(`/executions/${executionId}`);
      setToast({ show: true, message: 'Execution deleted successfully', color: 'success' });
      setExecutions(executions.filter(e => e.executionId !== executionId));
    } catch (error) {
      console.error('Error deleting execution:', error);
      setToast({ show: true, message: 'Failed to delete execution', color: 'danger' });
    }
  };

  const handleCurrentEventChange = (executionId, event) => {
    setCurrentEvents(prev => {
      if (event === null) {
        // Remove the event from state
        const { [executionId]: removed, ...rest } = prev;
        return rest;
      } else {
        // Add/update the event
        return {
          ...prev,
          [executionId]: event
        };
      }
    });
  };

  const getEventDescription = (event) => {
    if (!event) return null;
    
    // Prioritize reasoning and description fields
    if (event.reasoning) return event.reasoning;
    if (event.description) return event.description;
    if (event.step) return event.step;
    
    // Fallback to constructing from data
    const data = event.data || {};
    
    if (event.type === 'navigate' && data.url) {
      try {
        const urlObj = new URL(data.url);
        return `Navigate to ${urlObj.hostname}`;
      } catch {
        return `Navigate to ${data.url}`;
      }
    }
    if (event.type === 'click' && data.element) {
      return `Click: ${data.element}`;
    }
    if (event.type === 'type' && data.element) {
      return `Type in: ${data.element}`;
    }
    if (event.type === 'fill_form') {
      return `Fill form`;
    }
    if (event.type === 'snapshot') {
      return `Capture page`;
    }
    if (event.type === 'wait') {
      return `Wait for element`;
    }
    if (event.type === 'close') {
      return `Browser closed`;
    }
    
    return event.type || 'Unknown';
  };

  if (executionsLoading && executions.length === 0) {
    return <SyncingBadge label="Syncing" />;
  }

  if (executions.length === 0 && project) {
    return (
      <div className="executions-empty">
        {/* Same gss-header markup as WorkflowDemo so the empty-state
            heading is visually identical across Pipelines + Test Runs.
            Keeps the cube + heading at the same x-baseline and type
            scale instead of ad-hoc inline styles. */}
        <div className="gss-header">
          <IonIcon icon={cubeOutline} className="gss-header-icon" />
          <span className="gss-header-text">Get started in 3 simple steps</span>
        </div>
        
        <TerminalDemo apiToken={project.apiToken} />
      </div>
    );
  }

  return (
    <div className="executions-container">
      <div className="executions-grid">
        {executions.map((execution) => (
          <div 
            key={execution.executionId}
            className="execution-card"
            onClick={() => handleExecutionClick(execution.executionId)}
            onMouseLeave={() => handleCurrentEventChange(execution.executionId, null)}
          >
            <div className="execution-thumbnail">
              {execution.urls?.video ? (
                <HoverVideo 
                  videoUrl={execution.urls.video} 
                  executionId={execution.executionId}
                  onCurrentEventChange={(event) => handleCurrentEventChange(execution.executionId, event)}
                />
              ) : (
                <div className="execution-thumbnail-placeholder">
                  <IonIcon icon={playOutline} />
                </div>
              )}
            </div>
            
            <IonButton
              className="execution-delete-btn"
              fill="clear"
              onClick={(e) => handleDeleteExecution(execution.executionId, e)}
            >
              <IonIcon icon={trashOutline} slot="icon-only" />
            </IonButton>

            <div className="execution-card-content">
              <h3 
                key={currentEvents[execution.executionId] ? currentEvents[execution.executionId].id : execution.executionId}
                className="execution-title"
              >
                {currentEvents[execution.executionId] 
                  ? getEventDescription(currentEvents[execution.executionId])
                  : (execution.name || execution.testName || execution.executionId)
                }
              </h3>
              <p className="execution-time">{timeAgo(execution.createdAt)}</p>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="executions-load-more">
          <IonButton 
            expand="block" 
            fill="outline"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? <IonSpinner /> : 'Load More'}
          </IonButton>
        </div>
      )}
    </div>
  );
};

export default ProjectRunsList;

